import React, { useState, useEffect, useRef } from 'react';
import { 
  Container, Typography, Button, TextField, Box, Paper, Accordion, AccordionSummary, 
  AccordionDetails, IconButton, Divider, Grid,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import { CanvasModelViewer } from './ThreeJSViewer';
import { calculateModelVolume } from '../Utils/VolumeCalculator';
import { calculatePA12Price, calculateStandardPrice, PROCESSING_FEE_PA12, SHIPPING_COST, validateBuildSpace, PA12_BUILD_SPACE, SILICONE_BUILD_SPACE } from '../Utils/PriceCalculator';

// Import images
import silikon1Image from '../images/Silikon2.jpg';
import silikon3Image from '../images/silikon3.jpg';
import pa12Image from '../images/pa12.jpg';
import MaterialSelectionModal from './MaterialSelectionModal';

// Materialien mit zusätzlichen Informationen
const materials = [
  { 
    id: 1, 
    name: 'Silikon AR-G1L mit 35 Shore A', 
    supportPricePerCm3: 0.15,
    image: silikon1Image, 
    color: '#00000',
    temperature: '200 °C',
    additionalInfo: `Weiches, flexibles Silikon für elastische Anwendungen.`,
    isSilicone: true,
    isPlastic: false
  },
  { 
    id: 2, 
    name: 'Silikon AR-G1H mit 65 Shore A', 
    supportPricePerCm3: 0.18,
    image: silikon3Image, 
    color: '#00000',
    temperature: '150 °C',
    additionalInfo: `Härteres Silikon für stabilere Formteile.`,
    isSilicone: true,
    isPlastic: false
  },
  { 
    id: 3, 
    name: 'PA12', 
    pricePerCubicCm: 0.22, 
    image: pa12Image, 
    color: '#00000',
    additionalInfo: `Tauchlackierungen kostenneutral in den Grundfarben möglich.`,
    isPlastic: true,
    isSilicone: false
  },
];

interface ModelConfig {
  id: number;
  file: File;
  volume?: number;
  dimensions?: { width: number; height: number; depth: number };
  material?: number;
  quantity: number;
}

function App2() {
  const [models, setModels] = useState<ModelConfig[]>([]);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [materialModalOpen, setMaterialModalOpen] = useState<boolean>(false);
  const [currentModelId, setCurrentModelId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  
  // Add a helper function for price calculation
  const calculateItemPrice = (model: ModelConfig, material: typeof materials[0] | null | undefined): number => {
    if (!material) return 0;
    
    if (material.isPlastic && model.dimensions) {
      return calculatePA12Price(model.dimensions, model.quantity);
    } else if (material.isSilicone && model.volume && model.dimensions) {
      return calculateStandardPrice(
        model.volume,
        model.quantity,
        model.dimensions.depth,
      );
    }
    return 0;
  };

  // Aktualisierung des Gesamtpreises
  useEffect(() => {
    const calculatedPrice = models.reduce((total, model) => {
      if (model.material) {
        const selectedMaterial = materials.find(m => m.id === model.material);
        return total + calculateItemPrice(model, selectedMaterial);
      }
      return total;
    }, 0);
    
    const hasPA12 = models.some(model => {
      const material = materials.find(m => m.id === model.material);
      return material?.isPlastic;
    });
    
    const processingFee = hasPA12 ? PROCESSING_FEE_PA12 : 0;
    
    setTotalPrice(calculatedPrice + SHIPPING_COST + processingFee);
  }, [models]);
  
  // Hilfsfunktion zum Verarbeiten von Dateien (für Upload & Drag&Drop)
  const processFiles = async (files: FileList | File[]) => {
    try {
      for (const file of Array.from(files)) {
        const { volume, dimensions } = await calculateModelVolume(file);
        const newModel: ModelConfig = {
          id: Date.now() + Math.floor(Math.random() * 1000),
          file,
          volume,
          dimensions,
          quantity: 1
        };
        setModels(prev => [...prev, newModel]);
      }
    } catch (error) {
      console.error('Error processing 3D model:', error);
      // Add appropriate error handling UI here
    }
  };

  // Handler für Datei-Upload
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      await processFiles(event.target.files);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  // Handler für Drag & Drop
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };
  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      await processFiles(e.dataTransfer.files);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  // Add a function to format the price
  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };
  
  // Handler für Material-Auswahl öffnen
  const handleOpenMaterialModal = (modelId: number) => {
    setCurrentModelId(modelId);
    setMaterialModalOpen(true);
  };
  
  // Handler für Material-Auswahl
  const handleMaterialSelect = (materialId: number) => {
    if (currentModelId !== null) {
      const model = models.find(m => m.id === currentModelId);
      const selectedMaterial = materials.find(m => m.id === materialId);
      
      // Check build space limits
      if (model?.dimensions && selectedMaterial) {
        const validation = validateBuildSpace(
          model.dimensions, 
          Boolean(selectedMaterial.isSilicone)
        );
        
        if (!validation.valid && validation.message) {
          alert(validation.message);
          return;
        }
      }

      setModels(models.map(model => 
        model.id === currentModelId ? { ...model, material: materialId } : model
      ));
      setMaterialModalOpen(false);
    }
  };
  
  // Handler für Mengenänderung
  const handleQuantityChange = (modelId: number, newQuantity: number | string) => {
    const quantity = typeof newQuantity === 'string' 
      ? parseInt(newQuantity, 10) 
      : newQuantity;
    
    if (isNaN(quantity) || quantity < 1) return;
    
    setModels(models.map(model => 
      model.id === modelId ? { ...model, quantity } : model
    ));
  };
  
  // Handler für Modell-Löschung
  const handleDeleteModel = (modelId: number) => {
    setModels(models.filter(model => model.id !== modelId));
  };
  
  return (
    <Container maxWidth="lg">
      <Box my={4}>
        <Typography variant="h3" align="center" gutterBottom>
          3D-Druck Kostenrechner
        </Typography>
        
        {/* Upload-Bereich */}
        <Paper
          elevation={3}
          sx={{
            p: 3,
            mb: 3,
            textAlign: 'center',
            border: isDragActive ? '2px dashed #1976d2' : 'none',
            backgroundColor: isDragActive ? '#e3f2fd' : 'inherit',
            transition: 'border 0.2s, background-color 0.2s',
            cursor: 'pointer',
          }}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            accept=".stl,.obj,.3mf"
            style={{ display: 'none' }}
            id="upload-file"
            type="file"
            multiple
            onChange={handleFileUpload}
            ref={fileInputRef}
          />
          <label htmlFor="upload-file">
            <Button
              variant="contained"
              component="span"
              startIcon={<CloudUploadIcon />}
              size="large"
            >
              3D-Modell hochladen
            </Button>
          </label>
          <Typography variant="caption" display="block" mt={1}>
            Unterstützte Formate: STL, OBJ, 3MF
          </Typography>
          {isDragActive && (
            <Typography variant="body2" color="primary" mt={2}>
              Dateien hierher ziehen und ablegen
            </Typography>
          )}
        </Paper>
        
        {/* Modell-Liste als Akkordeons */}
        {models.length > 0 && (
          <Box mb={4}>
            <Typography variant="h5" gutterBottom>
              Hochgeladene Modelle
            </Typography>
            
            {models.map((model, index) => {
              const selectedMaterial = model.material 
                ? materials.find(m => m.id === model.material) 
                : null;

              return (
                <Accordion key={model.id} defaultExpanded={true}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                      <Typography sx={{ flexGrow: 1 }}>
                        {model.file.name}
                      </Typography>
                      {selectedMaterial && (
                        <Box sx={{ 
                          width: 16, 
                          height: 16, 
                          borderRadius: '50%', 
                          backgroundColor: selectedMaterial.color,
                          mr: 1,
                          display: 'inline-block'
                        }} />
                      )}
                      <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                        {selectedMaterial ? `${selectedMaterial.name}, ${model.quantity} Stk.` : 'Kein Material ausgewählt'}
                      </Typography>
                      <Typography variant="body2" color="text.primary" sx={{ fontWeight: 'bold' }}>
                        {selectedMaterial?.isPlastic && model.dimensions
                          ? formatPrice(calculatePA12Price(model.dimensions, model.quantity))
                          : selectedMaterial && model.volume
                            ? formatPrice(calculateStandardPrice(model.volume, model.quantity, model.dimensions?.depth))
                            : '—'}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={3}>
                      {/* 3D-Vorschau */}
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ height: 200, border: '1px solid #eee', borderRadius: 1 }}>
                          <CanvasModelViewer file={model.file} material={selectedMaterial?.color || '#cccccc'} zoomFactor={1.5} />
                        </Box>
                      </Grid>
                      
                      {/* Modellinformationen und Steuerung */}
                      <Grid item xs={12} sm={8}>
                        <Box display="flex" flexDirection="column" height="100%" justifyContent="space-between">
                          {/* Info-Bereich */}
                          <Box>
                            <Typography variant="subtitle1" gutterBottom>
                              Modellinformationen
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Volumen: {model.volume?.toFixed(2)} cm³
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Abmessungen: {model.dimensions?.width.toFixed(2)} x {model.dimensions?.height.toFixed(2)} x {model.dimensions?.depth.toFixed(2)} mm
                            </Typography>
                            {model.dimensions && (
                              <>
                                {/* Size validation warnings */}
                                {(() => {
                                  const pa12Validation = validateBuildSpace(model.dimensions, false);
                                  const siliconValidation = validateBuildSpace(model.dimensions, true);
                                  
                                  if (!pa12Validation.valid && !siliconValidation.valid) {
                                    return (
                                      <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                        Warnung: Modell überschreitet die maximalen Bauräume aller verfügbaren Materialien
                                      </Typography>
                                    );
                                  }
                                  return null;
                                })()}
                              </>
                            )}
                            <Typography variant="body2" color="text.secondary">
                              {selectedMaterial
                                  ? `Material: ${selectedMaterial.name}`
                                  : 'Material: Nicht ausgewählt'}
                            </Typography>
                          </Box>
                          
                          {/* Steuerelemente */}
                          <Box display="flex" justifyContent="space-between" alignItems="flex-end" mt={2}>
                            {/* Material-Auswahl */}
                            <Button
                              variant="outlined"
                              startIcon={<ColorLensIcon />}
                              onClick={() => handleOpenMaterialModal(model.id)}
                              size="small"
                            >
                              {selectedMaterial ? 'Material ändern' : 'Material wählen'}
                            </Button>
                            
                            {/* Mengensteuerung und Löschen */}
                            <Box display="flex" alignItems="center">
                              <TextField
                                type="number"
                                label="Anzahl"
                                variant="outlined"
                                size="small"
                                value={model.quantity}
                                onChange={(e) => handleQuantityChange(model.id, e.target.value)}
                                inputProps={{ min: 1 }}
                                sx={{ width: 100, mr: 2 }}
                              />
                              
                              <IconButton
                                color="error"
                                onClick={() => handleDeleteModel(model.id)}
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </Box>
                          
                          {/* Preis */}
                          {selectedMaterial && (
                            <Typography variant="h6" align="right" mt={2}>
                              Preis: {formatPrice(
                                selectedMaterial
                                  ? calculateItemPrice(model, selectedMaterial)
                                  : 0
                              )}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        )}
        
        {/* Zusammenfassung und Bestellung */}
        {models.length > 0 && (
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              Zusammenfassung
            </Typography>
            
            {/* Bestellübersicht */}
            <Box sx={{ mb: 3 }}>
              {models.map((model) => {
                const selectedMaterial = model.material 
                  ? materials.find(m => m.id === model.material) 
                  : null;
                
                const modelPrice = calculateItemPrice(model, selectedMaterial);

                return (
                  <Box key={model.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>
                      {model.file.name} - {selectedMaterial?.name || 'Kein Material'} x {model.quantity}
                    </Typography>
                    <Typography>
                      {formatPrice(modelPrice)}
                    </Typography>
                  </Box>
                );
              })}

              <Divider sx={{ my: 2 }} />
              
              {/* Calculate total processing fees */}
              {(() => {
                const hasPA12 = models.some(model => {
                  const material = materials.find(m => m.id === model.material);
                  return material?.isPlastic;
                });

                const basePrice = models.reduce((total, model) => {
                  const selectedMaterial = model.material 
                    ? materials.find(m => m.id === model.material) 
                    : null;
                  
                  return total + calculateItemPrice(model, selectedMaterial);
                }, 0);

                return (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="h6">
                        Zwischensumme:
                      </Typography>
                      <Typography variant="h6">
                        {formatPrice(basePrice)}
                      </Typography>
                    </Box>

                    {hasPA12 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="h6">
                          Nachbearbeitung:
                        </Typography>
                        <Typography variant="h6">
                          {formatPrice(PROCESSING_FEE_PA12)}
                        </Typography>
                      </Box>
                    )}
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="h6">
                        Versandkosten:
                      </Typography>
                      <Typography variant="h6">
                        {formatPrice(SHIPPING_COST)}
                      </Typography>
                    </Box>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="h5" color="primary">
                        Geschätzer Gesamtpreis:
                      </Typography>
                      <Typography variant="h5" color="primary" fontWeight="bold">
                        {formatPrice(basePrice + (hasPA12 ? PROCESSING_FEE_PA12 : 0) + SHIPPING_COST)}
                      </Typography>
                    </Box>
                  </>
                );
              })()}
            </Box>

          </Paper>
        )}
        
        {/* Material-Auswahl-Modal */}
        <MaterialSelectionModal
          open={materialModalOpen}
          onClose={() => setMaterialModalOpen(false)}
          onSelect={handleMaterialSelect}
          currentModelId={currentModelId}
          models={models}
          formatPrice={formatPrice}
          materials={materials}
          calculateItemPrice={calculateItemPrice}
        />
      </Box>
      <Box mt={6} mb={2} textAlign="center">
        <Typography variant="subtitle1" color="text.secondary">
          Für ein detailliertes Angebot kontaktieren Sie uns gerne.
        </Typography>
      </Box>
    </Container>
  );
}

export default App2;